import React from 'react';
import { X, Play, Star, Clock, Calendar, Users, Film, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from './FavoriteButton';

const ContentInfoModal = ({ content, onClose }) => {
  const navigate = useNavigate();

  if (!content) return null;

  const handleWatchNow = () => {
    onClose();
    navigate(`/content/${content.id}`);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal Content */}
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl bg-card/95 backdrop-blur-3xl border border-white/20 shadow-2xl shadow-electric/20"
        style={{ backdropFilter: 'blur(40px) saturate(180%)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="content-info-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors"
          data-testid="modal-close-button"
        >
          <X className="h-5 w-5 text-white" />
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Thumbnail */}
          <div className="md:w-2/5 relative">
            <div className="aspect-[2/3] md:aspect-auto md:h-full">
              <img
                src={content.thumbnail}
                alt={content.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent md:bg-gradient-to-r" />
            </div>
          </div>

          {/* Info Section */}
          <div className="flex-1 p-6 md:p-8 space-y-6 overflow-y-auto max-h-[60vh] md:max-h-[80vh]">
            {/* Title & Rating */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                {content.title}
              </h2>
              
              <div className="flex items-center flex-wrap gap-4 text-sm text-foreground/80">
                <div className="flex items-center space-x-1">
                  <Star className="h-4 w-4 text-electric fill-electric" />
                  <span className="font-semibold">{content.rating.toFixed(1)}</span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>{content.year}</span>
                </div>
                
                {content.duration && (
                  <div className="flex items-center space-x-1">
                    <Clock className="h-4 w-4" />
                    <span>{content.duration}</span>
                  </div>
                )}
                
                <div className="flex items-center space-x-1">
                  <Film className="h-4 w-4" />
                  <span className="capitalize">{content.content_type.replace('_', ' ')}</span>
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {content.featured && (
                <span className="px-3 py-1 rounded-full bg-pulse/20 text-pulse text-sm font-medium border border-pulse/30">
                  Featured
                </span>
              )}
              {content.is_premium && (
                <span className="px-3 py-1 rounded-full bg-electric/20 text-electric text-sm font-medium border border-electric/30">
                  Premium
                </span>
              )}
              {content.is_ppv && (
                <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium border border-amber-500/30">
                  $4.99 PPV
                </span>
              )}
            </div>

            {/* Genres */}
            <div className="flex flex-wrap gap-2">
              {content.genre.map((genre, index) => (
                <span
                  key={index}
                  className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm text-foreground"
                >
                  {genre}
                </span>
              ))}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Synopsis</h3>
              <p className="text-foreground/80 leading-relaxed">
                {content.description}
              </p>
            </div>

            {/* Cast & Director */}
            {(content.cast || content.director) && (
              <div className="space-y-3">
                {content.director && (
                  <div className="flex items-start space-x-2">
                    <span className="text-foreground/60 text-sm min-w-[80px]">Director:</span>
                    <span className="text-foreground text-sm">{content.director}</span>
                  </div>
                )}
                
                {content.cast && content.cast.length > 0 && (
                  <div className="flex items-start space-x-2">
                    <Users className="h-4 w-4 text-foreground/60 mt-0.5" />
                    <span className="text-foreground/60 text-sm min-w-[60px]">Cast:</span>
                    <span className="text-foreground text-sm">{content.cast.join(', ')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center space-x-4 pt-4">
              <button
                onClick={handleWatchNow}
                className="flex items-center space-x-2 px-8 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-all hover:scale-105 shadow-lg shadow-electric/30"
                data-testid="modal-watch-button"
              >
                <Play className="h-5 w-5 fill-background" />
                <span>Watch Now</span>
              </button>
              
              <FavoriteButton contentId={content.id} size="lg" showText />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentInfoModal;
